import { priceProject } from './price-project';

export type DualPriceEntity = {
    min_price_soles?: number | null;
    min_price_dolares?: number | null;
    price_soles?: number | null;
    price_dolares?: number | null;
    type_currency?: number | null;
    minPricePEN?: number | null;
    minPriceUSD?: number | null;
    price?: number | null;
};

export type DualPriceDisplayMode = 'soles' | 'dolares' | 'dual' | 'empty';

export type DualPriceDisplay = {
    mode: DualPriceDisplayMode;
    soles?: string;
    dolares?: string;
};

export const DUAL_PRICE_SEPARATOR = ' ó ';

function isDeclaredAmount(value: number | null | undefined): value is number {
    return value !== null && value !== undefined && !Number.isNaN(Number(value));
}

function firstDeclared(
    ...values: Array<number | null | undefined>
): number | null {
    for (const value of values) {
        if (isDeclaredAmount(value)) return Number(value);
    }
    return null;
}

function formatSolesLabel(amount: number): string {
    return `S/ ${priceProject(amount)}`;
}

function formatDolaresLabel(amount: number): string {
    return `US$ ${priceProject(amount)}`;
}

function resolveAmounts(entity?: DualPriceEntity | null): {
    soles: number | null;
    dolares: number | null;
} {
    if (!entity) return { soles: null, dolares: null };

    const soles = firstDeclared(entity.price_soles, entity.min_price_soles);
    const dolares = firstDeclared(
        entity.price_dolares,
        entity.min_price_dolares
    );

    if (soles !== null || dolares !== null) {
        return { soles, dolares };
    }

    const typeCurrency = Number(entity.type_currency);
    if (typeCurrency === 2) {
        const usd = firstDeclared(entity.price, entity.minPriceUSD);
        return { soles: null, dolares: usd };
    }

    const pen = firstDeclared(entity.price, entity.minPricePEN);
    return { soles: pen, dolares: null };
}

/** Formato N90-2430: S/ X | US$ Y | S/ X ó US$ Y (una línea) */
export function formatDualPrice(
    soles?: number | null,
    dolares?: number | null
): string {
    const hasSoles = isDeclaredAmount(soles);
    const hasDolares = isDeclaredAmount(dolares);
    const solesLabel = hasSoles ? formatSolesLabel(Number(soles)) : '';
    const dolaresLabel = hasDolares
        ? formatDolaresLabel(Number(dolares))
        : '';

    if (hasSoles && hasDolares) {
        return `${solesLabel}${DUAL_PRICE_SEPARATOR}${dolaresLabel}`;
    }
    if (hasSoles) return solesLabel;
    if (hasDolares) return dolaresLabel;
    return '';
}

export function resolveDualPriceDisplay(
    entity?: DualPriceEntity | null
): DualPriceDisplay {
    const { soles, dolares } = resolveAmounts(entity);
    const hasSoles = isDeclaredAmount(soles);
    const hasDolares = isDeclaredAmount(dolares);

    if (hasSoles && hasDolares) {
        return {
            mode: 'dual',
            soles: formatSolesLabel(Number(soles)),
            dolares: formatDolaresLabel(Number(dolares))
        };
    }
    if (hasSoles) {
        return { mode: 'soles', soles: formatSolesLabel(Number(soles)) };
    }
    if (hasDolares) {
        return {
            mode: 'dolares',
            dolares: formatDolaresLabel(Number(dolares))
        };
    }
    return { mode: 'empty' };
}

/**
 * Prioriza montos duales declarados. Si ambos son null (proyecto legacy),
 * muestra solo la moneda original — sin USD referencial convertido con TC.
 */
export function formatEntityDualPrice(entity?: DualPriceEntity | null): string {
    const display = resolveDualPriceDisplay(entity);
    if (display.mode === 'dual') {
        return `${display.soles}${DUAL_PRICE_SEPARATOR}${display.dolares}`;
    }
    if (display.mode === 'soles') return display.soles || '';
    if (display.mode === 'dolares') return display.dolares || '';
    return '';
}
